function Privacy() {
  return (
    <div className="min-h-screen px-6 py-12 text-white bg-black">
      <div className="max-w-4xl mx-auto space-y-12 bg-gray-900/50 border border-white/20 rounded-2xl p-8 md:p-12">
        {/* Header */}
        <div className="flex flex-col items-center text-center">
          <h1 className="relative text-4xl font-bold px-4 py-2">🔒 Privacy & Data Security</h1>
          <p className="text-center text-sm text-white/70 mt-2">Effective Date: Friday, August 8, 2025</p>
          <p className="max-w-2xl mt-6 text-lg text-purple-300">
            VintariAI is built on a <span className="font-semibold">zero-knowledge design</span>:
            your conversations are encrypted on your device before they ever reach our servers.
            Not even our team can read your chats.
          </p>
        </div>

        {/* 1. Data Collection */}
        <section>
          <h2 className="text-2xl font-semibold mb-3">1. Data We Collect</h2>
          <p className="text-white/70 italic mb-4">Only what’s needed to provide and secure your service.</p>
          <ul className="list-disc pl-5 space-y-2 text-white/80">
            <li><strong>Email address</strong> for authentication (via Supabase Auth).</li>
            <li>
              <strong>Encrypted</strong> chat titles, prompts, and responses, plus a
              <strong> conversation creation timestamp</strong> (not per-message).
            </li>
            <li><strong>Usage counts</strong> (e.g., messages per day) to enforce fair use and prevent abuse.</li>
            <li><span className="text-purple-400 font-semibold">No error logs, tracking, or personal profiling</span> are collected.</li>
            <li>Your password is never stored by us; Supabase Auth manages credentials securely.</li>
          </ul>
          <div className="mt-4 text-white/70">
            <span className="text-white/80 font-medium">Example:</span> If you brainstorm a new product idea with VintariAI,
            we don’t see it, only that a conversation exists with a creation time.
          </div>
        </section>

        {/* 2. Auth & Access */}
        <section>
          <h2 className="text-2xl font-semibold mb-3">2. Authentication & Access Control</h2>
          <p className="text-white/70 italic mb-4">Your account is yours alone.</p>
          <ul className="list-disc pl-5 space-y-2 text-white/80">
            <li>Secure login handled by <strong>Supabase Auth</strong>.</li>
            <li><strong>Row Level Security (RLS)</strong> isolates each user’s data.</li>
            <li>All database requests require verified <strong>bearer tokens</strong>.</li>
            <li><strong>Service keys are never exposed</strong> to the frontend.</li>
          </ul>
        </section>

        {/* 3. Encryption */}
        <section>
          <h2 className="text-2xl font-semibold mb-3">3. Encryption & Zero-Knowledge Protection</h2>
          <p className="text-white/70 italic mb-4">Your strategic work is unreadable to everyone but you.</p>
          <ul className="list-disc pl-5 space-y-2 text-white/80">
            <li><strong>AES-256 client-side encryption</strong> for chats and session metadata before data leaves your browser.</li>
            <li>Each record uses a unique key and IV; HTTPS secures data in transit; Supabase encrypts at rest.</li>
            <li>Because encryption happens client-side, <strong>VintariAI administrators cannot decrypt your chats</strong>.</li>
          </ul>
          <div className="mt-4 text-white/70">
            <span className="text-white/80 font-medium">Example:</span> If a competitor tried to subpoena your data from us,
            we’d have nothing readable to hand over, only you hold the key.
          </div>
        </section>

        {/* 4. Rights & Control */}
        <section>
          <h2 className="text-2xl font-semibold mb-3">4. Your Rights & Control</h2>
          <p className="text-white/70 italic mb-4">You own your data. Period.</p>
          <ul className="list-disc pl-5 space-y-2 text-white/80">
            <li><strong>Instant deletion:</strong> delete chats or your entire account and it’s removed immediately, no backups, no recovery window.</li>
            <li><strong>Export anytime</strong> from your settings.</li>
            <li>
              Exercise GDPR/CCPA rights via our secure{" "}
              <a
                href="https://docs.google.com/forms/d/e/1FAIpQLScYdVuvpyoKV3q04QaVoedAXePxGFOVeTP5uUC0EOFIpyNkYA/viewform?usp=header"
                target="_blank" rel="noopener noreferrer"
                className="underline bg-white/10 text-white/90 px-1.5 py-0.5 rounded"
              >
                Data Rights Request Form
              </a>.
            </li>
          </ul>
          <div className="mt-4 text-white/70">
            <span className="text-white/80 font-medium">Example:</span> Closing your account is like shredding your notes in front of you, instant and permanent.
          </div>
        </section>

        {/* 5. Retention & Usage */}
        <section>
          <h2 className="text-2xl font-semibold mb-3">5. Data Retention & Usage</h2>
          <ul className="list-disc pl-5 space-y-2 text-white/80">
            <li>Chats remain while your account is active unless you delete them.</li>
            <li>We store conversation creation timestamps for organization and <strong>usage counts</strong> to prevent abuse.</li>
            <li>For free-tier accounts, chat history is temporary and will reset whenever the tab is refreshed.</li>
          </ul>
        </section>

        {/* 6. Safety */}
        <section>
          <h2 className="text-2xl font-semibold mb-3">6. Safety & Abuse Prevention</h2>
          <p className="text-white/70 italic mb-4">Safety without sacrificing privacy.</p>
          <ul className="list-disc pl-5 space-y-2 text-white/80">
            <li><strong>Automated moderation runs before encryption</strong> to block illegal or unsafe prompts.</li>
            <li>We cannot access your chats; if you ever need help, you decide what to share with support.</li>
          </ul>
        </section>

        {/* 7. Third Parties */}
        <section>
          <h2 className="text-2xl font-semibold mb-3">7. Third-Party Infrastructure</h2>
          <ul className="list-disc pl-5 space-y-2 text-white/80">
            <li><strong>Supabase</strong> provides authentication and encrypted storage; it cannot decrypt your chats.</li>
            <li>
              <strong>OpenAI Systems:</strong> VintariAI works with OpenAI’s systems to generate responses and identify unsafe content. 
              All requests are anonymized, never used for training, and automatically purged by OpenAI within{" "}
              <span className="font-semibold">30 days</span>. This retention is governed by OpenAI and is independent of your VintariAI account deletion.
            </li>
          </ul>
        </section>

{/* 8. Comparison */}
<section>
  <h2 className="text-2xl font-semibold mb-3">8. How We Compare</h2>
  <div className="overflow-x-auto">
    <table className="w-full border border-white/20 text-white/80 text-sm md:text-base">
      <thead className="bg-white/10 text-white">
        <tr>
          <th className="p-3 text-left">Platform</th>
          <th className="p-3 text-left">Data Use</th>
          <th className="p-3 text-left">Retention</th>
          <th className="p-3 text-left">Access</th>
        </tr>
      </thead>
      <tbody>
        <tr className="border-t border-white/20">
          <td className="p-3 font-semibold">⚠️ ChatGPT (OpenAI app)</td>
          <td className="p-3">
            Stores chats by default and <strong>uses them to train models</strong>. 
            Memory stores personal details across sessions.
          </td>
          <td className="p-3">Until user deletes; 30 days if history disabled</td>
          <td className="p-3">Provider can access for training/safety</td>
        </tr>
        <tr className="border-t border-white/20">
          <td className="p-3 font-semibold">⚠️ Claude (Anthropic)</td>
          <td className="p-3">
            Branded as private, but staff may <strong>review your chats </strong> 
            “for safety.” You’re never fully invisible.
          </td>
          <td className="p-3">Fixed retention windows (up to 2 years for orgs)</td>
          <td className="p-3">Anthropic staff can access for review</td>
        </tr>
        <tr className="border-t border-white/20">
          <td className="p-3 font-semibold">⚠️ Gemini (Google)</td>
          <td className="p-3">
            Broad data collection: links AI activity with your 
            <strong> personal Google identity</strong> for profiling.
          </td>
          <td className="p-3">Default 18 months (3–36 months adjustable)</td>
          <td className="p-3">Google ties usage to your full account</td>
        </tr>
        <tr className="border-t border-purple-500/40 bg-purple-900/10">
          <td className="p-3 font-semibold text-purple-300">🔒 VintariAI</td>
          <td className="p-3">
            <strong>Zero-knowledge encryption:</strong> chats encrypted 
            client-side with unique keys. No training, no profiling, 
            no leaks, your strategy stays yours.
          </td>
          <td className="p-3">Forever or delete instantly with one click</td>
          <td className="p-3">Only you hold the key, no admin or provider access</td>
        </tr>
      </tbody>
    </table>

<p className="text-center text-purple-300 mt-4 italic text-sm">
  Most AI platforms <span className="text-red-400 font-semibold">store your chats by default and use them to train models</span>.  
  VintariAI is different, <strong>encrypted at the source</strong>, with keys that never leave your device.
</p>



  </div>
</section>



       {/* 9. Summary Box */}
<section className="bg-purple-900/30 border border-purple-500/50 rounded-lg p-6">
  <h2 className="text-2xl font-bold text-center text-purple-300 mb-4">
    What This Means For You
  </h2>
  <ul className="space-y-3 text-lg text-center">
    <li className="flex items-center justify-center gap-3">
      <span className="text-purple-300">🛡️</span>
      <span>Your strategy sessions are <strong>completely private</strong>.</span>
    </li>
    <li className="flex items-center justify-center gap-3">
      <span className="text-purple-300">🗝️</span>
      <span>Even our admins <strong>cannot access</strong> your ideas.</span>
    </li>
    <li className="flex items-center justify-center gap-3">
      <span className="text-purple-300">⚡</span>
      <span><strong>Instant</strong> export or deletion, no backups, no waiting.</span>
    </li>
  </ul>
</section>


        {/* 10. Updates */}
        <section>
          <h2 className="text-2xl font-semibold mb-3">9. Policy Updates</h2>
          <p className="text-white/80">
            If this policy changes, we’ll update the effective date and notify users of material changes.
          </p>
        </section>

        {/* Back Button */}
        <div className="pt-10 text-center">
          <div className="relative group inline-block">
            <div className="absolute -inset-1.5 bg-purple-600 rounded-lg blur-lg opacity-75 group-hover:opacity-100 transition duration-200"></div>
            <button
              onClick={() => (window.location.href = "/")}
              className="relative bg-black text-white font-semibold px-6 py-3 rounded-lg shadow hover:bg-gray-900 transition"
            >
              ← Back to Home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Privacy;
